import { useCallback, useMemo } from 'react';
import { useSettings } from './useSettings';
import { translations, TranslationParams, DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/translations';

export const useTranslation = () => {
  const { getSetting } = useSettings();

  // Get current language from settings, fallback to default
  const currentLanguage = useMemo(() => {
    const langSetting = getSetting('lang', DEFAULT_LANGUAGE);
    return SUPPORTED_LANGUAGES.includes(langSetting as SupportedLanguage)
      ? langSetting as SupportedLanguage
      : DEFAULT_LANGUAGE;
  }, [getSetting]);

  // Handle pluralization logic
  const handlePluralization = useCallback((text: string, count: number): string => {
    // Handle {s} pluralization pattern
    if (text.includes('{s}')) {
      return text.replace(/\{s\}/g, count === 1 ? '' : 's');
    }

    // Handle conditional pluralization based on specific patterns
    // This covers cases like "voto/votos", "photo/photos", etc.
    return text;
  }, []);

  // Handle variable interpolation
  const interpolateVariables = useCallback((text: string, params: TranslationParams = {}): string => {
    let result = text;

    // Replace all {variable} patterns with actual values
    Object.entries(params).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, String(value));
    });

    return result;
  }, []);

  // Main translation function
  const t = useCallback((key: string, params: TranslationParams = {}): string => {
    // Get translation for current language
    const translationEntry = translations[key];
    if (!translationEntry) {
      console.warn(`Translation key "${key}" not found`);
      return key; // Return key as fallback
    }

    let translatedText = translationEntry[currentLanguage] || translationEntry[DEFAULT_LANGUAGE];
    if (!translatedText) {
      console.warn(`Translation for key "${key}" not found in language "${currentLanguage}"`);
      return key; // Return key as fallback
    }

    // Handle special pluralization cases based on count parameter
    if (params.count !== undefined) {
      const count = Number(params.count);

      // Handle specific pluralization keys
      if (key === 'userLikesCount' || key.includes('Vote') || key.includes('vote')) {
        if (key === 'userLikesCount') {
          const votesKey = count === 1 ? 'voteReceived' : 'votesReceived';
          const votesText = translations[votesKey]?.[currentLanguage] || translations[votesKey]?.[DEFAULT_LANGUAGE] || 'votes';
          params.votes = votesText;
        }
      }

      // Apply pluralization rules
      translatedText = handlePluralization(translatedText, count);
    }

    // Apply variable interpolation
    translatedText = interpolateVariables(translatedText, params);

    return translatedText;
  }, [currentLanguage, handlePluralization, interpolateVariables]);

  // Helper function to get plural form of a word based on count
  const plural = useCallback((singularKey: string, pluralKey: string, count: number): string => {
    const key = count === 1 ? singularKey : pluralKey;
    return t(key);
  }, [t]);

  return {
    t,
    plural,
    currentLanguage,
    isRTL: false // None of our supported languages are RTL
  };
};
